import { useRef, useState, useEffect } from "react";
import { createNews } from "../../services/newsServices";
import { toast } from "react-hot-toast";
import RichTextEditor from "../../components/admin/common/RichTextEditor";
import FileUpload from "../../components/admin/common/FileUpload";
import CustomSelect from "../../components/admin/common/CustomSelect";
import Input from "../../components/admin/common/Input";
import Button from "../../components/admin/common/Button";
import Cookies from "js-cookie";

const publicOptions = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const galleryNameOptions = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const NewsFormPopup = ({ isOpen, onClose, shouldRefetch }) => {
  const imageRef = useRef();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [galleryName, setGalleryName] = useState(galleryNameOptions[0]);
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState(null);
  const [image, setImage] = useState([]);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({
    title: "",
    date: "",
    galleryName: "",
    description: "",
    visibility: "",
    image: "",
  });

  // Hide body overflow when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Validate all fields
  const validate = () => {
    const newErrors = {};
    if (!title) newErrors.title = "Title is required";
    if (!date) newErrors.date = "Date is required";
    if (!galleryName) newErrors.galleryName = "Please choose Yes or No";
    if (!description) newErrors.description = "Description is required";
    if (!visibility) newErrors.visibility = "Please select Public or Private";
    if (!image || image.length === 0)
      newErrors.image = "Please upload at least one image";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const memberId = Cookies.get("userId");

    const payload = {
      title,
      description,
      date,
      showOnHomepage: galleryName.value === "yes",
      imageUrl: image,
      visibility: visibility.value,
      status: "pending",
      memberId,
    };

    try {
      setLoading(true);
      await createNews(payload);
      await shouldRefetch();
      toast.success("News created successfully!");
      onClose();
      setTitle("");
      setDate("");
      setGalleryName(galleryNameOptions[0]);
      setDescription("");
      setVisibility(null);
      setImage([]);
      setErrors({});
    } catch (error) {
      toast.error(error.message || "Failed to create news");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setTitle("");
    setDate("");
    setGalleryName(galleryNameOptions[0]);
    setDescription("");
    setVisibility(null);
    setImage([]);
    setErrors({});
    onClose();
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Centered */}
        <div className="flex items-center justify-center relative p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Add New News</h2>
          <button
            onClick={handleCancel}
            className="absolute right-6 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="bg-[#F5F0FF] p-6 font-sans space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Title"
                value={title}
                onChange={(val) => {
                  setTitle(val);
                  if (errors.title) setErrors({ ...errors, title: "" });
                }}
                required
                error={errors.title}
              />
              <Input
                label="Date"
                type="date"
                value={date}
                onChange={(val) => {
                  setDate(val);
                  if (errors.date) setErrors({ ...errors, date: "" });
                }}
                required
                error={errors.date}
              />
              <CustomSelect
                label="Show on Home page ?"
                value={galleryName}
                onChange={(val) => {
                  setGalleryName(val);
                  if (errors.galleryName)
                    setErrors({ ...errors, galleryName: "" });
                }}
                options={galleryNameOptions}
                placeholder="Yes / No"
                error={errors.galleryName}
              />
            </div>

            <RichTextEditor
              description={description}
              setDescription={(val) => {
                setDescription(val);
                if (errors.description)
                  setErrors({ ...errors, description: "" });
              }}
            />
            {errors.description && (
              <p className="text-red font-semibold text-sm mt-1">
                {errors.description}
              </p>
            )}

            <div className="grid grid-cols-1 w-1/2 md:grid-cols-2 gap-4">
              <CustomSelect
                label="Public / Private"
                value={visibility}
                onChange={(val) => {
                  setVisibility(val);
                  if (errors.visibility)
                    setErrors({ ...errors, visibility: "" });
                }}
                options={publicOptions}
                placeholder="Select"
                error={errors.visibility}
              />
            </div>

            <FileUpload
              ref={imageRef}
              mode="image"
              defaultLabel="Choose Images"
              onUpload={() => {}}
              setImage={(img) => {
                setImage(img);
                if (errors.image) setErrors({ ...errors, image: "" });
              }}
              image={image}
            />
            {errors.image && (
              <p className="text-red font-semibold text-sm mt-1">
                {errors.image}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-white space-y-2">
          <Button className="w-full" onClick={handleSubmit} disabled={loading}>
            {loading ? "Submitting..." : "Confirm & Add News"}
          </Button>
          <Button className="w-full" variant="text" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewsFormPopup;
